import React, { useEffect, useState } from "react";
import { Button, Input, Select, Radio, message, Popconfirm } from 'antd';
import moment from "moment";
import fetchProfiles from '../common/helpers/fetch-profiles';
import fetchMarkets from '../common/helpers/fetch-profile-markets';
import createTriangulars from '../common/helpers/create-triangulars';
import editTriangular from '../common/helpers/edit-triangular';
import removeTriangular from '../common/helpers/remove-triangular';
import cancelTriangularOrder from '../common/helpers/cancel-triangular-order';
import createUnprocessedOrders from '../common/helpers/create-unprocessed-orders';

const tableHeaderStyle = { fontSize: 14, borderTop: "1px solid #cccccc", borderBottom: "1px solid #cccccc", color: "white", textAlign: "left", paddingLeft: 12 };
const tableRowStyle = { fontSize: 14, borderBottom: "1px solid #444444", color: "white", textAlign: "left", paddingLeft: 12 };
const labelStyle = { color: "white", marginBottom: 6 };
const fieldStyle = { marginBottom: 14 };

// Quotes the target market off the price the whole size could actually be hedged for
// through the two other markets, so the size is fixed rather than a min/max range: a
// range whose upper end is unhedgeable would just idle.
const emptyStrategy = (botId, userId) => ({
    id: null,
    bot_id: botId,
    user_id: userId,
    strategy_type: 'triangular',
    symbol: null,
    side: 'buy',
    size: null,
    size_unit: 'base',
    spread: null,
    sensitivity: null,
    timer: 60,
    price_increment_unit: null,
    balance_limit: null,
    cooldown_period: 3,
    depth_buffer: 1.5,
    depth_levels: 25,
    max_leg_slippage: 0.5,
    hedge_ratio: 1,
    legs: [
        { type: 'base_leg', symbol: null, profile: null, order_type: 'market' },
        { type: 'quote_leg', symbol: null, profile: null, order_type: 'market' }
    ],
    logs: [],
    saved: false,
    paused: false
});

const assetsOf = (symbol) => (symbol || '').split('/').map(a => (a || '').toLowerCase());

// Mirrors resolveTriangle on the bot side: the legs must close a triangle with the
// target market through exactly one shared intermediate asset.
const validateTriangle = (strategy) => {
    const [base, quote] = assetsOf(strategy.symbol);
    if (!base || !quote) return 'Select the target market';

    const [legA, legB] = strategy.legs;
    if (!legA.symbol || !legB.symbol) return 'Select both liquidity markets';
    if (!legA.profile || !legB.profile) return 'Select a hedge profile for both liquidity markets';

    const a = assetsOf(legA.symbol);
    const b = assetsOf(legB.symbol);

    const baseLeg = [a, b].find(l => l.includes(base) && !l.includes(quote));
    const quoteLeg = [a, b].find(l => l.includes(quote) && !l.includes(base));
    if (!baseLeg || !quoteLeg || baseLeg === quoteLeg) {
        return `${legA.symbol} and ${legB.symbol} do not form a triangle with ${strategy.symbol}`;
    }

    const mid = baseLeg.find(x => x !== base);
    if (!mid || mid !== quoteLeg.find(x => x !== quote)) {
        return 'The two liquidity markets share no intermediate asset';
    }

    return null;
};

const Triangular = ({ selectedBot, orders, setOrders, botState, disableAction, setDisableAction, userId }) => {
    const [profiles, setProfiles] = useState([]);
    const [markets, setMarkets] = useState([]);
    const [legMarkets, setLegMarkets] = useState({});
    const [draft, setDraft] = useState(null);

    const rows = orders.filter(order => order?.bot_id === selectedBot.id);

    useEffect(() => {
        fetchProfiles().then(res => { if (Array.isArray(res)) setProfiles(res); });
        fetchMarkets(selectedBot.host.id).then(res => { if (Array.isArray(res)) setMarkets(res); });
    }, [selectedBot.host.id]);

    // Leg markets come from the venue the hedge will actually run on, which is the same
    // venue the depth is measured on.
    const loadLegMarkets = (profileId) => {
        if (!profileId || legMarkets[profileId]) return;
        fetchMarkets(profileId).then(res => {
            if (Array.isArray(res)) setLegMarkets(prev => ({ ...prev, [profileId]: res }));
        });
    };

    const updateDraft = (patch) => setDraft(prev => ({ ...prev, ...patch }));

    const updateLeg = (index, patch) => setDraft(prev => {
        const legs = prev.legs.map((leg, i) => i === index ? { ...leg, ...patch } : leg);
        return { ...prev, legs };
    });

    const persist = (nextRows) => {
        setOrders(nextRows);
        createUnprocessedOrders(nextRows.filter(row => row.saved));
    };

    const saveDraft = async () => {
        const error = validateTriangle(draft);
        if (error) { message.error(error); return; }
        if (!(parseFloat(draft.size) > 0)) { message.error('Size must be greater than zero'); return; }
        if (draft.spread == null || isNaN(parseFloat(draft.spread))) { message.error('Set a spread'); return; }

        const strategy = {
            ...draft,
            id: draft.id || `${selectedBot.id}-${Date.now()}`,
            size: parseFloat(draft.size),
            spread: parseFloat(draft.spread),
            sensitivity: draft.sensitivity == null || draft.sensitivity === '' ? null : parseFloat(draft.sensitivity),
            timer: draft.timer == null || draft.timer === '' ? null : parseFloat(draft.timer),
            depth_buffer: parseFloat(draft.depth_buffer) || 1.5,
            depth_levels: parseFloat(draft.depth_levels) || 25,
            max_leg_slippage: draft.max_leg_slippage === '' ? null : parseFloat(draft.max_leg_slippage),
            hedge_ratio: parseFloat(draft.hedge_ratio) || 1,
            cooldown_period: parseFloat(draft.cooldown_period) || 3,
            saved: true,
            logs: draft.logs || []
        };

        const isEdit = rows.some(row => row.id === strategy.id);
        const nextRows = isEdit
            ? orders.map(row => row.id === strategy.id ? strategy : row)
            : [...orders, strategy];

        persist(nextRows);
        setDraft(null);

        // A running bot picks the change up immediately; otherwise it is applied on start.
        if (botState === 'play') {
            const res = isEdit ? await editTriangular(strategy) : await createTriangulars([strategy]);
            if (res?.error) message.error(res.error);
            else message.success(isEdit ? 'Strategy updated' : 'Strategy started');
        }
    };

    const deleteRow = async (row) => {
        persist(orders.filter(order => order.id !== row.id));
        if (botState === 'play') await removeTriangular({ id: row.id, bot_id: selectedBot.id });
    };

    const cancelRow = async (row) => {
        setDisableAction(true);
        const res = await cancelTriangularOrder(row.id, selectedBot.id);
        if (res?.error) message.error(res.error);
        setDisableAction(false);
    };

    const marketOptions = (list) => (list || []).map(m => ({ label: m, value: m }));

    const legLabel = (index) => index === 0 ? 'Base liquidity market' : 'Quote liquidity market';
    const legHint = (index) => {
        const [base, quote] = assetsOf(draft?.symbol);
        if (!base || !quote) return '';
        return index === 0
            ? `Prices ${base.toUpperCase()} against the intermediate asset (e.g. BTC/USDT)`
            : `Converts the intermediate asset to ${quote.toUpperCase()} (e.g. USDT/TRY)`;
    };

    return (
        <div style={{ margin: "10px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ color: "white", fontWeight: 600 }}>
                    TRIANGULAR STRATEGIES
                    <span style={{ color: "grey", fontWeight: 400, marginLeft: 10, fontSize: 12 }}>
                        Quotes one market off the depth-walked hedge price of two others
                    </span>
                </div>
                <Button
                    disabled={!!draft}
                    onClick={() => setDraft(emptyStrategy(selectedBot.id, userId))}
                    style={{ backgroundColor: "#161616", color: "white", borderRadius: 10 }}>
                    ADD STRATEGY
                </Button>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={{ ...tableHeaderStyle, width: "14%" }}>TARGET</th>
                        <th style={{ ...tableHeaderStyle, width: "8%" }}>SIDE</th>
                        <th style={{ ...tableHeaderStyle, width: "12%" }}>SIZE</th>
                        <th style={{ ...tableHeaderStyle, width: "8%" }}>SPREAD</th>
                        <th style={{ ...tableHeaderStyle, width: "22%" }}>LIQUIDITY LEGS</th>
                        <th style={{ ...tableHeaderStyle, width: "14%" }}>HEDGE PRICE</th>
                        <th style={{ ...tableHeaderStyle, width: "10%" }}>STATUS</th>
                        <th style={{ ...tableHeaderStyle, width: "12%" }}>ACTION</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 &&
                        <tr><td colSpan={8} style={{ ...tableRowStyle, color: "grey", padding: 20 }}>No triangular strategies yet.</td></tr>
                    }
                    {rows.map(row => {
                        const last = (row.logs || [])[row.logs.length - 1];
                        const status = last?.order_status || (row.saved ? 'idle' : 'unsaved');
                        const isOpen = status === 'open';

                        return (
                            <tr key={row.id}>
                                <td style={tableRowStyle}>{row.symbol || '-'}</td>
                                <td style={{ ...tableRowStyle, color: row.side === 'buy' ? '#38761d' : '#c11919' }}>{(row.side || '-').toUpperCase()}</td>
                                <td style={tableRowStyle}>{row.size} {row.size_unit === 'quote' ? assetsOf(row.symbol)[1]?.toUpperCase() : assetsOf(row.symbol)[0]?.toUpperCase()}</td>
                                <td style={tableRowStyle}>{row.spread}%</td>
                                <td style={tableRowStyle}>{(row.legs || []).map(leg => leg.symbol).filter(Boolean).join('  +  ') || '-'}</td>
                                <td style={tableRowStyle}>
                                    {last?.hedge_price ? Number(last.hedge_price).toPrecision(8) : '-'}
                                    {last?.legs_depth_used != null &&
                                        <div style={{ color: "grey", fontSize: 11 }}>
                                            depth used {(Number(last.legs_depth_used) * 100).toFixed(1)}%
                                        </div>
                                    }
                                </td>
                                <td style={{ ...tableRowStyle, color: isOpen ? 'white' : 'grey' }}>
                                    {status}
                                    {last?.created_at && <div style={{ color: "grey", fontSize: 11 }}>{moment(last.created_at).format('h:mm:ss a')}</div>}
                                </td>
                                <td style={tableRowStyle}>
                                    <Button
                                        size="small"
                                        disabled={disableAction || !!draft}
                                        onClick={() => { setDraft({ ...row }); (row.legs || []).forEach(leg => loadLegMarkets(leg.profile?.id)); }}
                                        style={{ backgroundColor: "#161616", color: "white", marginRight: 6 }}>
                                        Edit
                                    </Button>
                                    {isOpen &&
                                        <Button size="small" disabled={disableAction} onClick={() => cancelRow(row)}
                                            style={{ backgroundColor: "#161616", color: "white", marginRight: 6 }}>
                                            Cancel
                                        </Button>
                                    }
                                    <Popconfirm title="Delete this strategy?" onConfirm={() => deleteRow(row)}>
                                        <Button size="small" style={{ backgroundColor: "#161616", color: "#c11919" }}>Delete</Button>
                                    </Popconfirm>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {draft &&
                <div style={{ backgroundColor: "#222222", padding: 20, marginTop: 20, borderRadius: 10 }}>
                    <div style={{ color: "white", fontWeight: 600, marginBottom: 16 }}>
                        {draft.id ? 'EDIT STRATEGY' : 'NEW STRATEGY'}
                    </div>

                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                        <div style={{ ...fieldStyle, minWidth: 240 }}>
                            <div style={labelStyle}>Target market (quoted by this bot)</div>
                            <Select
                                showSearch
                                style={{ width: "100%" }}
                                value={draft.symbol}
                                placeholder="BTC/TRY"
                                options={marketOptions(markets)}
                                onChange={(value) => updateDraft({ symbol: value })}
                            />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>On {selectedBot?.host?.name}</div>
                        </div>

                        <div style={{ ...fieldStyle, minWidth: 160 }}>
                            <div style={labelStyle}>Side</div>
                            <Radio.Group value={draft.side} onChange={(e) => updateDraft({ side: e.target.value })}>
                                <Radio style={{ color: "white" }} value="buy">Buy</Radio>
                                <Radio style={{ color: "white" }} value="sell">Sell</Radio>
                            </Radio.Group>
                        </div>

                        <div style={{ ...fieldStyle, minWidth: 260 }}>
                            <div style={labelStyle}>Size</div>
                            <Input.Group compact>
                                <Input
                                    style={{ width: "55%" }}
                                    value={draft.size}
                                    placeholder="0.5"
                                    onChange={(e) => updateDraft({ size: e.target.value })}
                                />
                                <Select
                                    style={{ width: "45%" }}
                                    value={draft.size_unit}
                                    onChange={(value) => updateDraft({ size_unit: value })}
                                    options={[
                                        { label: assetsOf(draft.symbol)[0]?.toUpperCase() || 'Base', value: 'base' },
                                        { label: assetsOf(draft.symbol)[1]?.toUpperCase() || 'Quote', value: 'quote' }
                                    ]}
                                />
                            </Input.Group>
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>
                                Fixed size. Orders are only placed when this much can be hedged on both legs.
                            </div>
                        </div>

                        <div style={{ ...fieldStyle, minWidth: 140 }}>
                            <div style={labelStyle}>Spread (%)</div>
                            <Input value={draft.spread} placeholder="0.4" onChange={(e) => updateDraft({ spread: e.target.value })} />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Applied to the hedgeable price</div>
                        </div>
                    </div>

                    <div style={{ color: "white", fontWeight: 600, margin: "16px 0 10px" }}>LIQUIDITY LEGS</div>
                    {draft.legs.map((leg, index) => (
                        <div key={index} style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 12 }}>
                            <div style={{ minWidth: 240 }}>
                                <div style={labelStyle}>{legLabel(index)}</div>
                                <Select
                                    showSearch
                                    style={{ width: "100%" }}
                                    value={leg.symbol}
                                    placeholder={index === 0 ? "BTC/USDT" : "USDT/TRY"}
                                    options={marketOptions(legMarkets[leg.profile?.id])}
                                    onChange={(value) => updateLeg(index, { symbol: value })}
                                />
                                <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>{legHint(index)}</div>
                            </div>

                            <div style={{ minWidth: 220 }}>
                                <div style={labelStyle}>Hedge profile</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={leg.profile?.id}
                                    placeholder="Exchange API profile"
                                    options={profiles.map(p => ({ label: `${p.name} (${p.exchange})`, value: p.id }))}
                                    onChange={(value) => {
                                        loadLegMarkets(value);
                                        // Symbol is cleared because the market list belongs to the venue.
                                        updateLeg(index, { profile: { id: value }, symbol: null });
                                    }}
                                />
                                <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Prices and hedges are both taken here</div>
                            </div>

                            <div style={{ minWidth: 150 }}>
                                <div style={labelStyle}>Hedge order type</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={leg.order_type}
                                    onChange={(value) => updateLeg(index, { order_type: value })}
                                    options={[{ label: 'Market', value: 'market' }, { label: 'Limit', value: 'limit' }]}
                                />
                            </div>
                        </div>
                    ))}

                    <div style={{ color: "white", fontWeight: 600, margin: "16px 0 10px" }}>CANCELLATION RULE</div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Timer (sec)</div>
                            <Input value={draft.timer} placeholder="60" onChange={(e) => updateDraft({ timer: e.target.value })} />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Max age of a resting order</div>
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Sensitivity (%)</div>
                            <Input value={draft.sensitivity} placeholder="0.2" onChange={(e) => updateDraft({ sensitivity: e.target.value })} />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Cancel if the hedgeable price moves this far</div>
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Cooldown (sec)</div>
                            <Input value={draft.cooldown_period} onChange={(e) => updateDraft({ cooldown_period: e.target.value })} />
                        </div>
                    </div>

                    <div style={{ color: "white", fontWeight: 600, margin: "16px 0 10px" }}>ADVANCED</div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Depth buffer (x)</div>
                            <Input value={draft.depth_buffer} onChange={(e) => updateDraft({ depth_buffer: e.target.value })} />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Require this much more depth than needed</div>
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Book levels</div>
                            <Input value={draft.depth_levels} onChange={(e) => updateDraft({ depth_levels: e.target.value })} />
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Max leg slippage (%)</div>
                            <Input value={draft.max_leg_slippage} onChange={(e) => updateDraft({ max_leg_slippage: e.target.value })} />
                            <div style={{ color: "grey", fontSize: 11, marginTop: 4 }}>Alerts only; hedges always run</div>
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Price increment</div>
                            <Input value={draft.price_increment_unit} onChange={(e) => updateDraft({ price_increment_unit: e.target.value })} />
                        </div>
                        <div style={{ minWidth: 160 }}>
                            <div style={labelStyle}>Balance limit</div>
                            <Input value={draft.balance_limit} onChange={(e) => updateDraft({ balance_limit: e.target.value })} />
                        </div>
                    </div>

                    <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
                        <Button onClick={saveDraft} style={{ backgroundColor: "#161616", color: "white", borderRadius: 10 }}>SAVE</Button>
                        <Button onClick={() => setDraft(null)} style={{ backgroundColor: "#161616", color: "grey", borderRadius: 10 }}>CANCEL</Button>
                    </div>
                </div>
            }
        </div>
    );
};

export default Triangular;
